import React from "react";
import { useTranslation } from "react-i18next";
import {
  CartItem,
  PaymentLedger,
  SelectedCartItem,
} from "@/shared/utils/interfaces";
import { formatPrice } from "@/shared/utils/helpers";
import { useCartItemTotal } from "@/shared/hooks/useCartHook";
import Center from "../Base/Center";
import { cn } from "@/shared/utils/cn";

interface SplitPaymentCartItemsProps {
  splitPaymentCart: CartItem[];
  selectedCartItems: SelectedCartItem[];
  paymentLedgers: PaymentLedger[];
  handleCartSplit: (cartItem: CartItem, ledgerId: number) => void;
}

const SplitPaymentCartItems: React.FC<SplitPaymentCartItemsProps> = ({
  splitPaymentCart,
  selectedCartItems,
  paymentLedgers,
  handleCartSplit,
}) => {
  const { t } = useTranslation();
  const getCartItemTotal = useCartItemTotal();

  const getAssignedQty = (cartItemId: number, ledgerId: number) => {
    return (
      selectedCartItems.find(
        (c) => c.item_id === cartItemId && c.ledgerId === ledgerId
      )?.quantity || 0
    );
  };

  return (
    <div className="my-8 flex flex-col gap-4">
      {splitPaymentCart.map((cartItem, index) => {
        return (
          <div
            key={cartItem.item_id}
            className="flex items-center justify-between p-2"
            style={{
              borderBottom:
                index === splitPaymentCart.length - 1
                  ? "none"
                  : "1px solid var(--grey)",
            }}
          >
            <div>
              <div className="font-semibold text-[1.8rem]">{cartItem.name}</div>
              <SplitPaymentCartToppingsItem cartItem={cartItem} />
              <span className="text-default">
                {cartItem.quantity} x{" "}
                {formatPrice(getCartItemTotal(cartItem) / cartItem.quantity)}
              </span>
            </div>
            <div className="flex gap-4">
              {paymentLedgers.map(({ item_id: ledgerId }, index) => {
                const assignedQty = getAssignedQty(cartItem.item_id, ledgerId);
                return (
                  <div
                    key={ledgerId}
                    onClick={() => handleCartSplit(cartItem, ledgerId)}
                    className={cn(
                      "p-[0.75rem_1.5rem] rounded-[0.5rem] cursor-pointer relative font-medium text-2xl",
                      assignedQty > 0
                        ? "bg-primary text-white"
                        : "bg-dark/20 text-dark"
                    )}
                  >
                    {t("posOrders.person")}
                    {index + 1}
                    {assignedQty > 0 && (
                      <Center className="absolute -top-3.5 -right-3 bg-error text-white rounded-full size-9 font-bold">
                        {assignedQty}
                      </Center>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SplitPaymentCartToppingsItem = ({ cartItem }: { cartItem: CartItem }) => {
  return cartItem?.sub_content ? (
    <div className="text-[1.3rem] italic">
      {cartItem?.sub_content?.map((tp, i, array) => (
        <span key={tp.item_id}>
          {`${tp.name}`}
          {array.length !== i + 1 ? `, ` : "."}
        </span>
      ))}
    </div>
  ) : null;
};

export default SplitPaymentCartItems;
